import { useState } from "react";
import { Code2, Server, Globe, Zap, Shield, GitBranch, Lock, BarChart3, Cpu, Database } from "lucide-react";

const categories = [
  {
    id: "hosting",
    label: "Eamico Cloud",
    icon: Server,
    headline: "Deploy globally with a single command",
    desc: "Edge-optimized cloud powered by the Aethernet — our patent-pending technology that lets people own part of the internet and earn from it. Automatic scaling, instant rollbacks, and global reach.",
    features: [
      { icon: Globe, title: "300+ Edge Locations", text: "Your code runs where your users are. Sub-50ms latency globally" },
      { icon: Shield, title: "DDoS Protection", text: "Enterprise-grade security included on every plan, at no extra cost" },
      { icon: BarChart3, title: "Real-time Analytics", text: "Monitor traffic, performance, and errors as they happen" },
    ],
  },
  {
    id: "all",
    label: "Platform",
    icon: Database,
    headline: "One platform. Everything you need.",
    desc: "From code to production in minutes. Eamico takes your code and ships it to a global edge network — so you can focus on building, not managing infrastructure.",
    features: [
      { icon: Lock, title: "Auto SSL", text: "Every deployment gets automatic HTTPS. Zero configuration needed" },
      { icon: Zap, title: "Instant Deploys", text: "Sub-second deployments. Push to main and you're live" },
      { icon: GitBranch, title: "Preview Branches", text: "Every pull request gets its own URL. Review before you ship" },
    ],
  },
];

const ProductsSection = () => {
  const [active, setActive] = useState("hosting");
  const current = categories.find((c) => c.id === active);

  return (
    <section id="products" className="bg-white py-16 sm:py-20 lg:py-24" data-testid="products-section">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-8">
        {/* Section header */}
        <div className="max-w-2xl">
          <h2 className="text-[28px] sm:text-[34px] lg:text-[40px] font-normal text-[#202124] leading-tight">
            Build with the platform designed for{" "}
            <span className="text-[#ff9a4d]">developers</span>
          </h2>
          <p className="mt-4 text-[15px] sm:text-base text-[#5f6368] leading-relaxed">
            Try Eamico with free usage of all products, plus early adopters get priority access on launch.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <a href="#cta" className="inline-flex items-center px-5 py-2.5 bg-[#ff9a4d] hover:bg-[#e8872e] text-white text-[14px] font-medium rounded-md transition-colors" data-testid="products-get-started">
              Get started free
            </a>
            <a href="#products" className="inline-flex items-center px-5 py-2.5 text-[14px] font-medium text-[#ff9a4d] hover:text-[#e8872e] transition-colors" data-testid="products-see-all">
              See all products
            </a>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-12 border-b border-[#e8eaed]">
          <div className="flex gap-0 overflow-x-auto" data-testid="products-tabs">
            {categories.map((cat) => {
              const Icon = cat.icon;
              return (
                <button
                  key={cat.id}
                  onClick={() => setActive(cat.id)}
                  className={`flex items-center gap-2 px-5 py-3.5 text-[14px] font-medium whitespace-nowrap border-b-[3px] transition-colors ${
                    active === cat.id
                      ? "text-[#ff9a4d] border-[#ff9a4d]"
                      : "text-[#5f6368] border-transparent hover:text-[#202124] hover:border-[#dadce0]"
                  }`}
                  data-testid={`products-tab-${cat.id}`}
                >
                  <Icon className="w-4 h-4" />
                  {cat.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab content */}
        <div className="mt-10 grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          {/* Left — text */}
          <div key={current.id}>
            <h3 className="text-[22px] sm:text-[26px] font-normal text-[#202124] leading-snug" data-testid="products-content-title">
              {current.headline}
            </h3>
            <p className="mt-4 text-[15px] text-[#5f6368] leading-relaxed max-w-lg">
              {current.desc}
            </p>
            <a href="#cta" className="inline-flex items-center mt-6 text-[14px] font-medium text-[#ff9a4d] hover:text-[#e8872e] transition-colors" data-testid="products-content-cta">
              Learn more
              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
            </a>
          </div>

          {/* Right — feature cards */}
          <div className="space-y-4" data-testid="products-features">
            {current.features.map((feat, i) => {
              const FIcon = feat.icon;
              return (
                <div
                  key={i}
                  className="flex gap-4 p-5 rounded-xl border border-[#e8eaed] hover:border-[#dadce0] hover:shadow-sm transition-all cursor-default"
                  data-testid={`product-feature-${i}`}
                >
                  <div className="w-10 h-10 rounded-lg bg-[#fff4eb] flex items-center justify-center shrink-0">
                    <FIcon className="w-5 h-5 text-[#ff9a4d]" />
                  </div>
                  <div>
                    <p className="text-[15px] font-medium text-[#202124]">{feat.title}</p>
                    <p className="text-[13px] text-[#5f6368] mt-1 leading-relaxed">{feat.text}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductsSection;
